import React, { useState, useContext } from "react";
import {
  makeStyles, Typography, Box, Paper, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button,
} from "@material-ui/core";
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from "@material-ui/icons";
import { Link as RouterLink } from "react-router-dom";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import QrcodeModal from "../../components/QrcodeModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    background: theme.mode === "light" ? "#f8fafc" : "#0f172a",
    padding: "32px 24px 48px",
    maxWidth: 800,
    margin: "0 auto",
  },
  backBtn: {
    display: "inline-flex", alignItems: "center", gap: 6,
    color: theme.mode === "light" ? "#64748b" : "#94a3b8",
    textDecoration: "none", fontSize: 13, fontWeight: 500, marginBottom: 20,
    transition: "color 0.15s",
    "&:hover": { color: "#2563eb" },
  },
  header: {
    display: "flex", alignItems: "flex-start", justifyContent: "space-between",
    marginBottom: 24, gap: 16, flexWrap: "wrap",
  },
  pageTitle: {
    fontWeight: 700, fontSize: 24,
    color: theme.mode === "light" ? "#0f172a" : "#f1f5f9",
    letterSpacing: "-0.02em", marginBottom: 4,
  },
  pageDesc: {
    fontSize: 13, color: theme.mode === "light" ? "#64748b" : "#94a3b8",
    lineHeight: 1.6,
  },
  addBtn: {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "10px 18px", borderRadius: 10,
    background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
    color: "#fff", fontWeight: 600, fontSize: 13, border: "none",
    cursor: "pointer", boxShadow: "0 2px 8px rgba(34,197,94,0.3)",
    transition: "opacity 0.15s", whiteSpace: "nowrap",
    "&:hover": { opacity: 0.88 },
  },
  card: {
    borderRadius: 14,
    border: `1px solid ${theme.mode === "light" ? "rgba(15,23,42,0.08)" : "rgba(255,255,255,0.08)"}`,
    boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 6px 16px rgba(0,0,0,0.05)",
    background: theme.mode === "light" ? "#ffffff" : "#1e293b",
    padding: "16px 18px",
    marginBottom: 12,
    display: "flex", alignItems: "center", justifyContent: "space-between",
    gap: 12, flexWrap: "wrap",
  },
  cardLeft: { display: "flex", alignItems: "center", gap: 14, flex: 1, minWidth: 0 },
  avatar: {
    width: 46, height: 46, borderRadius: 12,
    display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0,
  },
  name: {
    fontWeight: 700, fontSize: 14,
    color: theme.mode === "light" ? "#0f172a" : "#f1f5f9",
  },
  number: {
    fontSize: 12, color: theme.mode === "light" ? "#64748b" : "#94a3b8", marginTop: 2,
  },
  statusBadge: {
    display: "inline-flex", alignItems: "center", gap: 5,
    padding: "3px 10px", borderRadius: 20, marginTop: 5,
    fontSize: 11, fontWeight: 600,
  },
  actions: {
    display: "flex", gap: 6, flexShrink: 0,
    flexWrap: "wrap", justifyContent: "flex-end",
  },
  btn: {
    padding: "7px 13px", borderRadius: 8, fontSize: 12, fontWeight: 600,
    cursor: "pointer", border: "1.5px solid transparent", fontFamily: "inherit",
    transition: "all 0.15s", display: "inline-flex", alignItems: "center", gap: 4,
    "&:hover": { opacity: 0.82 },
  },
  iconBtn: {
    padding: "7px 10px", borderRadius: 8, fontSize: 12, fontWeight: 600,
    cursor: "pointer", border: "1.5px solid transparent", fontFamily: "inherit",
    transition: "all 0.15s", display: "inline-flex", alignItems: "center",
  },
}));

const STATUS_CONFIG = {
  CONNECTED:    { label: "Conectado",     bg: "#dcfce7", color: "#15803d", dot: "#22c55e" },
  qrcode:       { label: "Aguardando QR", bg: "#fef3c7", color: "#92400e", dot: "#f59e0b" },
  DISCONNECTED: { label: "Desconectado",  bg: "#fee2e2", color: "#b91c1c", dot: "#ef4444" },
  OPENING:      { label: "Conectando...", bg: "#dbeafe", color: "#1d4ed8", dot: "#3b82f6" },
  DEFAULT:      { label: "Desconhecido",  bg: "#f1f5f9", color: "#64748b", dot: "#94a3b8" },
};

const WA_ICON = (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

export default function ConfiguracoesWhatsApp() {
  const classes = useStyles();
  const { whatsApps, loading } = useContext(WhatsAppsContext);

  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedWA, setSelectedWA] = useState(null);
  const [startingQR, setStartingQR] = useState(null); // id loading

  // Add/Edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [editingWA, setEditingWA] = useState(null);
  const [editName, setEditName] = useState("");
  const [saving, setSaving] = useState(false);

  // Delete confirm
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingWA, setDeletingWA] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Open QR modal — for DISCONNECTED status, start session first (POST /whatsappsession/:id)
  const handleQR = async (wa) => {
    if (wa.status === "DISCONNECTED") {
      setStartingQR(wa.id);
      try {
        // POST starts the session and triggers QR generation
        await api.post(`/whatsappsession/${wa.id}`);
      } catch (err) {
        toastError(err);
        setStartingQR(null);
        return;
      }
      setStartingQR(null);
    }
    setSelectedWA(wa);
    setQrModalOpen(true);
  };

  const handleCloseQR = () => { setSelectedWA(null); setQrModalOpen(false); };

  // Reiniciar = clear session + restart (PUT /whatsappsession/:id)
  const handleRestart = async (wa) => {
    try {
      await api.put(`/whatsappsession/${wa.id}`);
      toast.success(`${wa.name} — reiniciando sessão...`);
    } catch (err) { toastError(err); }
  };

  // Desconectar = DELETE /whatsappsession/:id
  const handleDisconnect = async (wa) => {
    try {
      await api.delete(`/whatsappsession/${wa.id}`);
      toast.success(`${wa.name} desconectado`);
    } catch (err) { toastError(err); }
  };

  // Reconectar = PUT /whatsappsession/:id (mesma lógica do reiniciar)
  const handleReconnect = async (wa) => {
    try {
      await api.put(`/whatsappsession/${wa.id}`);
      toast.success(`Reconectando ${wa.name}...`);
    } catch (err) { toastError(err); }
  };

  const openEdit = (wa) => {
    setEditingWA(wa);
    setEditName(wa ? wa.name : "");
    setEditOpen(true);
  };

  const handleSave = async () => {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      if (editingWA) {
        await api.put(`/whatsapp/${editingWA.id}`, { name: editName });
        toast.success("Nome atualizado!");
      } else {
        const { data } = await api.post("/whatsapp/", {
          name: editName,
          status: "DISCONNECTED",
          isDefault: false,
          greetingMessage: "",
          farewellMessage: "",
        });
        toast.success("Conexão criada! Clique em QR Code para vincular.");
      }
      setEditOpen(false);
    } catch (err) { toastError(err); }
    finally { setSaving(false); }
  };

  const openDelete = (wa) => { setDeletingWA(wa); setDeleteOpen(true); };

  const handleDelete = async () => {
    if (!deletingWA) return;
    setDeleting(true);
    try {
      await api.delete(`/whatsapp/${deletingWA.id}`);
      toast.success(`${deletingWA.name} removido`);
      setDeleteOpen(false);
    } catch (err) { toastError(err); }
    finally { setDeleting(false); }
  };

  return (
    <div className={classes.root}>
      <RouterLink to="/configuracoes" className={classes.backBtn}>
        <ArrowBackIcon style={{ fontSize: 16 }} />
        Voltar para Configurações
      </RouterLink>

      <div className={classes.header}>
        <div>
          <Typography className={classes.pageTitle}>Conexões WhatsApp</Typography>
          <Typography className={classes.pageDesc}>
            Adicione números, escaneie QR Code, reinicie ou gerencie conexões.
          </Typography>
        </div>
        <button className={classes.addBtn} onClick={() => openEdit(null)}>
          <AddIcon style={{ fontSize: 17 }} />
          Nova Conexão
        </button>
      </div>

      {loading ? (
        <Box display="flex" justifyContent="center" padding={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {whatsApps.map((wa) => {
            const st = STATUS_CONFIG[wa.status] || STATUS_CONFIG.DEFAULT;
            const isLoading = startingQR === wa.id;
            return (
              <Paper key={wa.id} className={classes.card} elevation={0}>
                <Box className={classes.cardLeft}>
                  <Box className={classes.avatar}
                    style={{ background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)", boxShadow: "0 3px 10px rgba(34,197,94,0.25)" }}>
                    {WA_ICON}
                  </Box>
                  <Box style={{ minWidth: 0 }}>
                    <Typography className={classes.name}>{wa.name}</Typography>
                    <Typography className={classes.number}>
                      {wa.number ? "+" + wa.number : "Número não vinculado"}
                    </Typography>
                    <Box className={classes.statusBadge} style={{ background: st.bg, color: st.color }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: st.dot }} />
                      {st.label}
                    </Box>
                  </Box>
                </Box>

                <Box className={classes.actions}>
                  {/* QR Code — disponível quando não CONNECTED */}
                  {wa.status !== "CONNECTED" && (
                    <button
                      className={classes.btn}
                      style={{ background: "#eff6ff", color: "#1d4ed8", borderColor: "#bfdbfe" }}
                      onClick={() => handleQR(wa)}
                      disabled={isLoading}
                    >
                      {isLoading
                        ? <><CircularProgress size={12} /> Iniciando...</>
                        : <>📱 QR Code</>
                      }
                    </button>
                  )}

                  {/* Reiniciar — sempre disponível */}
                  <button
                    className={classes.btn}
                    style={{ background: "#fefce8", color: "#854d0e", borderColor: "#fde68a" }}
                    onClick={() => handleRestart(wa)}
                  >
                    <RefreshIcon style={{ fontSize: 13 }} /> Reiniciar
                  </button>

                  {/* Editar nome */}
                  <button
                    className={classes.btn}
                    style={{ background: "#f8fafc", color: "#475569", borderColor: "#e2e8f0" }}
                    onClick={() => openEdit(wa)}
                  >
                    <EditIcon style={{ fontSize: 13 }} /> Editar
                  </button>

                  {/* Desconectar — só quando CONNECTED */}
                  {wa.status === "CONNECTED" && (
                    <button
                      className={classes.btn}
                      style={{ background: "#fff7ed", color: "#c2410c", borderColor: "#fed7aa" }}
                      onClick={() => handleDisconnect(wa)}
                    >
                      Desconectar
                    </button>
                  )}

                  {/* Deletar */}
                  <button
                    className={classes.iconBtn}
                    style={{ background: "#fff1f2", color: "#b91c1c", borderColor: "#fecaca" }}
                    onClick={() => openDelete(wa)}
                    title="Remover conexão"
                  >
                    <DeleteIcon style={{ fontSize: 15 }} />
                  </button>
                </Box>
              </Paper>
            );
          })}

          {whatsApps.length === 0 && (
            <Paper elevation={0} style={{
              borderRadius: 14, border: "1px dashed #cbd5e1", padding: "48px 24px",
              textAlign: "center", background: "transparent"
            }}>
              <Box style={{ fontSize: 44, marginBottom: 12 }}>📱</Box>
              <Typography style={{ fontWeight: 700, fontSize: 15, color: "#0f172a", marginBottom: 6 }}>
                Nenhuma conexão cadastrada
              </Typography>
              <Typography style={{ fontSize: 13, color: "#64748b", marginBottom: 20 }}>
                Clique em "Nova Conexão" para adicionar um número de WhatsApp.
              </Typography>
              <button className={classes.addBtn} onClick={() => openEdit(null)} style={{ margin: "0 auto" }}>
                <AddIcon style={{ fontSize: 17 }} /> Nova Conexão
              </button>
            </Paper>
          )}
        </>
      )}

      {/* QR Modal */}
      {qrModalOpen && selectedWA && (
        <QrcodeModal
          open={qrModalOpen}
          onClose={handleCloseQR}
          whatsAppId={selectedWA.id}
        />
      )}

      {/* Add / Edit Modal */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle style={{ fontWeight: 700, paddingBottom: 8 }}>
          {editingWA ? "Editar Conexão" : "Nova Conexão WhatsApp"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus label="Nome da conexão" fullWidth variant="outlined" size="small"
            value={editName} onChange={e => setEditName(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleSave()}
            placeholder="Ex: Vendas, Suporte, Isabele..."
            style={{ marginTop: 8 }}
          />
          {!editingWA && (
            <Box style={{ marginTop: 12, padding: "10px 14px", background: "#f0fdf4", borderRadius: 8, border: "1px solid #bbf7d0" }}>
              <Typography style={{ fontSize: 12, color: "#15803d", lineHeight: 1.6 }}>
                ✅ Após criar, clique em <strong>QR Code</strong> na conexão para escanear com o WhatsApp e vincular o número.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions style={{ padding: "12px 20px 16px" }}>
          <Button onClick={() => setEditOpen(false)} style={{ textTransform: "none" }}>Cancelar</Button>
          <Button variant="contained" color="primary" onClick={handleSave}
            disabled={saving || !editName.trim()}
            style={{ textTransform: "none", fontWeight: 600, borderRadius: 8 }}>
            {saving ? <CircularProgress size={16} color="inherit" /> : editingWA ? "Salvar" : "Criar Conexão"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Modal */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle style={{ fontWeight: 700, paddingBottom: 8 }}>
          Remover Conexão
        </DialogTitle>
        <DialogContent>
          <Typography style={{ fontSize: 14, color: "#374151" }}>
            Tem certeza que deseja remover <strong style={{ color: "#111827" }}>{deletingWA?.name}</strong>?
          </Typography>
          <Typography style={{ fontSize: 12, color: "#ef4444", marginTop: 8 }}>
            ⚠️ Todos os fluxos e automações vinculados a esta conexão serão afetados.
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: "12px 20px 16px" }}>
          <Button onClick={() => setDeleteOpen(false)} style={{ textTransform: "none" }}>Cancelar</Button>
          <Button onClick={handleDelete} disabled={deleting}
            style={{ background: "#ef4444", color: "#fff", textTransform: "none", fontWeight: 600, borderRadius: 8 }}>
            {deleting ? <CircularProgress size={16} color="inherit" /> : "Remover"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
